import type { Mentoring } from '#shared/types/Mentoring'
import type { UserProfile } from '#shared/types/User'
import { RequestStatus } from '#shared/types/ParticipationRequest'
import { ChatTopicBase } from '#shared/types/Chat'
import { getTopicKey } from '#shared/chat'
import { useMentoring } from '@/composables/mentoring/useMentoring'
import { useChat } from '@/composables/chat/useChat'

// Locally-enriched mentoring: we augment with `_chatClosed` after looking up the
// per-mentoring chat doc, since chat closure (the canonical "Ukončený" signal in the UI)
// isn't denormalised onto the mentoring document.
type EnrichedMentoring = Mentoring & { _chatClosed?: boolean }

// State priority — top entry wins when the user has multiple live mentorings
type State =
    | 'partner_confirmed_only' // partner already pressed "Potvrď začiatok"; current user hasn't
    | 'accepted_confirm'       // both sides accepted, no-one confirmed start yet
    | 'matched_active'         // mentoring is running (both/either confirmed)
    | 'in_discussion'          // discussing terms before acceptance
    | 'requested'              // request sent, awaiting response

export interface NextAction {
    state: State
    label: string
    to: string
    icon: string
    partnerName?: string
    /** True for actions that require the user to do something now — drives banner emphasis. */
    urgent: boolean
}

// Treat a mentoring as ended whenever any of the end signals are set. Status alone is
// not enough: a closed-chat mentoring can keep `status: Accepted` while the chat doc
// has `mentoringClosure` set. `_chatClosed` is filled in by `ensureLoaded` below.
const isTerminal = (m: EnrichedMentoring) =>
    m.status === RequestStatus.Finished ||
    m.status === RequestStatus.Rejected ||
    m.status === RequestStatus.Expired ||
    m.status === RequestStatus.Cancelled ||
    Boolean(m.endedAt) ||
    Boolean(m.mentorEnded) ||
    Boolean(m._chatClosed)

// "Matched" = the mentoring has officially started. Confirming always sets both sides +
// startedAt in one click (no partner handshake), so startedAt is the single source of truth.
// A pair is therefore only ever "accepted, needs confirming" or "running" — never in between.
const isMatched = (m: Mentoring) => Boolean(m.startedAt)

// Per-user archive — written by archiveMentoringForUser in useMentoring.ts. Hides the
// mentoring from the user's dashboard without changing global status. Without this
// filter, archived rows came back on every reload because the source query still
// included them.
const isArchivedFor = (m: Mentoring, userId: string) =>
    Array.isArray(m.archivedByIds) && m.archivedByIds.includes(userId)

// Mirror ActivityTable's STALE_UNSTARTED_MENTORING_DAYS so the "Čakajúce / Aktívne /
// Dokončené" tiles on /profile/mentoring match what /profile/activity actually shows
// after clicking through — old unstarted requests are hidden there, so we fold them into
// "Dokončené" rather than "Čakajúce".
const STALE_UNSTARTED_MENTORING_DAYS = 90

const toMillis = (date: unknown): number => {
    if (!date) return 0
    const d = date as { toMillis?: () => number; toDate?: () => Date; seconds?: number }
    if (typeof d.toMillis === 'function') return d.toMillis()
    if (typeof d.toDate === 'function') return d.toDate().getTime()
    if (typeof d.seconds === 'number') return d.seconds * 1000
    if (date instanceof Date) return date.getTime()
    if (typeof date === 'number') return date
    return 0
}

const isStaleUnstartedMentoring = (m: Mentoring): boolean => {
    const createdMs = toMillis(m.createdAt as unknown)
    if (!createdMs) return false
    return Date.now() - createdMs > STALE_UNSTARTED_MENTORING_DAYS * 24 * 60 * 60 * 1000
}

const partnerNameOf = (m: Mentoring, userId: string) => {
    const userIsMentor = m.mentorId === userId
    const meta = userIsMentor ? m.menteeMeta : m.mentorMeta
    return `${meta?.firstName ?? ''} ${meta?.lastName ?? ''}`.trim() || 'partner'
}

/**
 * Reactive mentoring next-action state for any logged-in user. Drives `<MentoringDashboard />`
 * (the personalised hub rendered on homepage, /mentori, and /mentoring-network-pre-svetove-slovensko).
 *
 * Caches the mentoring list across components via `useState` so multiple consumers
 * don't trigger duplicate Firestore reads in the same page render.
 */
export function useTopMentoringNextAction() {
    const store = useApplicationStore()
    const { listMentoringsByMentor, listMentoringsByMentee } = useMentoring()
    const { getChatIdByTopicOnly, loadChat } = useChat()

    // Shared state across components on the same page.
    // `null` = never fetched yet; `[]` = fetched, no TOP mentorings.
    const cache = useState<EnrichedMentoring[] | null>('top-mentorings-cache', () => null)
    const loading = useState<boolean>('top-mentorings-loading', () => false)

    const userProfile = computed<UserProfile | null>(() => store.userProfile as UserProfile | null)
    const isTopApproved = computed(
        () => Boolean(userProfile.value?.mentorTopApproved || userProfile.value?.menteeTopApproved)
    )

    const ensureLoaded = async () => {
        if (cache.value !== null || loading.value || !store.userId) return
        loading.value = true
        try {
            // Load both sides for any logged-in user — next-action surfaces apply outside TOP
            // (e.g. a non-TOP mentor with an accepted mentoring should still see "Potvrď začiatok").
            // The TOP-specific stepper consumes a TOP-filtered subset via `buckets` below.
            const [mentorList, menteeList] = await Promise.all([
                listMentoringsByMentor(store.userId),
                listMentoringsByMentee(store.userId)
            ])
            const combined: EnrichedMentoring[] = [...(mentorList ?? []), ...(menteeList ?? [])]

            // For mentorings that would otherwise surface as action cards, look up the chat
            // doc and mark them closed if `mentoringClosure` is set. Without this, stale
            // pairs (chat ended but mentoring.status still `Accepted`) keep showing
            // "Potvrď začiatok" until the user clicks through and discovers it's "Ukončený".
            // TODO(refactor): this enrichment loop is duplicated in
            // app/pages/profile/mentoring/index.vue → onMounted. When this file changes, the
            // page-level copy needs the same change. Extract into a shared helper
            // (enrichWithChatClosure / useChatClosureEnrichment) and call from both sites.
            const candidatesForChatCheck = combined.filter(
                (m) =>
                    Boolean(m.id) &&
                    (m.status === RequestStatus.Accepted ||
                        m.status === RequestStatus.InDiscussion ||
                        isMatched(m))
            )
            await Promise.all(
                candidatesForChatCheck.map(async (m) => {
                    try {
                        const topic = getTopicKey(ChatTopicBase.mentoring, m.id!)
                        const chatId = await getChatIdByTopicOnly(topic)
                        if (!chatId) return
                        const chat = await loadChat(chatId)
                        if (chat?.mentoringClosure) m._chatClosed = true
                    } catch (err) {
                        // Don't fail the whole load just because one chat lookup misfired.
                        console.warn('useTopMentoringNextAction: chat closure lookup failed', m.id, err)
                    }
                })
            )

            cache.value = combined
        } catch (err) {
            console.error('useTopMentoringNextAction: failed to load mentorings', err)
            cache.value = []
        } finally {
            loading.value = false
        }
    }

    const nextAction = computed<NextAction | null>(() => {
        if (!cache.value || !store.userId) return null
        const userId = store.userId
        const live = cache.value.filter((m) => !isTerminal(m) && !isArchivedFor(m, userId))

        const matched = live.filter(isMatched)
        const accepted = live.filter((m) => m.status === RequestStatus.Accepted && !isMatched(m))

        // 1. Partner confirmed start, current user hasn't — most urgent (one click and we're running)
        const partnerOnly = matched.find((m) => {
            const userIsMentor = m.mentorId === userId
            const meConfirmed = userIsMentor ? m.startConfirmedByMentor : m.startConfirmedByMentee
            const themConfirmed = userIsMentor ? m.startConfirmedByMentee : m.startConfirmedByMentor
            return Boolean(themConfirmed) && !meConfirmed
        })
        if (partnerOnly) {
            return {
                state: 'partner_confirmed_only',
                label: 'Potvrď začiatok',
                to: `/profile/activity/mentoring/${partnerOnly.id}`,
                icon: 'heroicons:check-circle',
                partnerName: partnerNameOf(partnerOnly, userId),
                urgent: true
            }
        }

        // 2. Accepted, no-one confirmed yet — both sides need to press the button
        if (accepted.length > 0) {
            const target = accepted.length === 1 && accepted[0]!.id
                ? `/profile/activity/mentoring/${accepted[0]!.id}`
                : '/profile/activity?tab=confirm'
            return {
                state: 'accepted_confirm',
                label: 'Potvrď začiatok',
                to: target,
                icon: 'heroicons:check-circle',
                partnerName: accepted.length === 1 ? partnerNameOf(accepted[0]!, userId) : undefined,
                urgent: true
            }
        }

        // 3. Already running — gentle "open your mentoring"
        if (matched.length > 0) {
            return {
                state: 'matched_active',
                label: 'Otvor svoj mentoring',
                to: '/profile/mentoring',
                icon: 'heroicons:arrow-right',
                urgent: false
            }
        }

        // 4. In discussion — passive nudge
        const inDiscussion = live.find((m) => m.status === RequestStatus.InDiscussion)
        if (inDiscussion) {
            return {
                state: 'in_discussion',
                label: 'Pokračuj v diskusii',
                to: '/profile/activity?tab=confirm',
                icon: 'heroicons:chat-bubble-left-right',
                urgent: false
            }
        }

        // 5. Requested — fully passive; we render the pill but with quieter framing
        const requested = live.find((m) => m.status === RequestStatus.Requested)
        if (requested) {
            return {
                state: 'requested',
                label: 'Tvoja žiadosť čaká',
                to: '/profile/mentoring',
                icon: 'heroicons:clock',
                urgent: false
            }
        }

        return null
    })

    // TOP-filtered subset for the stepper. mentoringType isn't reliably written on all
    // creation paths; fall back to treating all of the user's mentorings as TOP when no
    // tagged ones are found AND the user is TOP-approved (relaxed-filter workaround).
    const topMentorings = computed<Mentoring[]>(() => {
        if (!cache.value) return []
        const tagged = cache.value.filter((m) => m.mentoringType === 'top')
        if (tagged.length > 0) return tagged
        return isTopApproved.value ? cache.value : []
    })

    // Buckets — drives TopProcessStepper / TopPaymentTile / TopActionCards for TOP users.
    const buckets = computed(() => {
        if (!cache.value || !store.userId) {
            return {
                matchedCount: 0,
                acceptedCount: 0,
                inDiscussionCount: 0,
                requestedCount: 0,
                actionableMentorings: [] as Mentoring[],
                runningMentorings: [] as Mentoring[],
                loaded: false
            }
        }
        const userId = store.userId
        const live = topMentorings.value.filter((m) => !isTerminal(m) && !isArchivedFor(m, userId))
        // isMatched === started, so these three are mutually exclusive and there is no
        // "one side confirmed, waiting for the other" bucket to surface.
        const matched = live.filter(isMatched)
        const accepted = live.filter((m) => m.status === RequestStatus.Accepted && !isMatched(m))
        const inDiscussionCount = live.filter((m) => m.status === RequestStatus.InDiscussion && !isMatched(m)).length
        const requestedCount = live.filter((m) => m.status === RequestStatus.Requested && !isMatched(m)).length

        return {
            matchedCount: matched.length,
            acceptedCount: accepted.length,
            inDiscussionCount,
            requestedCount,
            actionableMentorings: accepted,
            runningMentorings: matched,
            loaded: true
        }
    })

    // Cross-type activity tile counters (Čakajúce / Aktívne / Dokončené) for /profile/mentoring.
    // Counts across BOTH roles (not TOP-filtered) and excludes archived rows, mirroring the
    // legacy classifyMentoring on that page so the tiles agree with /profile/activity.
    const activityCounts = computed(() => {
        if (!cache.value || !store.userId) return { pending: 0, active: 0, completed: 0 }
        const userId = store.userId
        let pending = 0
        let active = 0
        let completed = 0
        for (const m of cache.value) {
            if (isArchivedFor(m, userId)) continue
            if (isTerminal(m)) {
                completed++
            } else if (m.startedAt || m.startConfirmedByMentor || m.startConfirmedByMentee) {
                active++
            } else if (isStaleUnstartedMentoring(m)) {
                // Old unstarted requests are hidden from "Treba vybaviť" on /profile/activity,
                // so fold them into "Dokončené" here to keep the tiles in sync.
                completed++
            } else {
                pending++
            }
        }
        return { pending, active, completed }
    })

    // True once the mentoring list has been fetched at least once this session. Drives the
    // stepper skeleton on /profile/mentoring (avoids Matching→Mentoring flicker).
    const loaded = computed(() => cache.value !== null)

    // Patch the shared cache after an action resolves on a card, so every surface consuming
    // this composable (homepage strip, /mentori, /profile/mentoring) reflects it immediately
    // without a refetch. Without this, the cached row keeps its pre-action fields and the
    // resolved mentoring reappears on the next surface that reads the shared cache.
    const applyResolution = (id: string, action: 'start' | 'reject' | 'archive') => {
        if (!cache.value) return
        const userId = store.userId
        cache.value = cache.value.map((m) => {
            if (m.id !== id) return m
            if (action === 'archive') {
                if (!userId) return m
                const ids = Array.isArray(m.archivedByIds) ? m.archivedByIds : []
                return ids.includes(userId) ? m : { ...m, archivedByIds: [...ids, userId] }
            }
            if (action === 'reject') {
                return { ...m, status: RequestStatus.Rejected }
            }
            // 'start' — mirrors TopActionCards.confirmStart: both sides confirmed + officially
            // started, so the pair moves out of "actionable" and into the running bucket.
            return {
                ...m,
                status: RequestStatus.Accepted,
                startConfirmedByMentor: true,
                startConfirmedByMentee: true,
                startedAt: m.startedAt ?? (new Date() as unknown as Mentoring['startedAt'])
            }
        })
    }

    // Cross-type actionable mentorings — used by MentoringDashboard so non-TOP users with
    // accepted-but-not-started pairs also see "Potvrď začiatok" cards.
    const actionableAllTypes = computed<Mentoring[]>(() => {
        if (!cache.value || !store.userId) return []
        const userId = store.userId
        const live = cache.value.filter((m) => !isTerminal(m) && !isArchivedFor(m, userId))
        return live.filter((m) => m.status === RequestStatus.Accepted && !isMatched(m))
    })

    return {
        ensureLoaded,
        nextAction,
        loading,
        loaded,
        isTopApproved,
        buckets,
        actionableAllTypes,
        activityCounts,
        applyResolution
    }
}
